import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { addTenancy, editTenancy, getTenancies } from '../service';
import { FormValues } from '../components/schemas';

function useTenancy(propertyId: number) {
    const query = useQuery({
        queryKey: ['property', propertyId, 'tenancy'],
        queryFn: () => getTenancies(propertyId),
    });

    return query;
}

function useAddTenancy(
    propertyId: number,
    {
        onClose,
        reset,
        onError,
    }: {
        onClose: () => void;
        reset: () => void;
        onError?: (error: Error) => void;
    }
) {
    const toast = useToast();
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (data: FormValues) => {
            await addTenancy(propertyId, data);
        },
        onSuccess: () => {
            toast({
                title: 'Tenancy added',
                description: 'Tenancy successfully added',
                status: 'success',
                isClosable: true,
            });

            void queryClient.invalidateQueries({
                queryKey: ['property', propertyId],
            });

            reset();
            onClose();
        },
        onError,
    });

    return {
        isPending: mutation.isPending,
        error: mutation.error,
        add: mutation.mutate,
        reset: mutation.reset,
    };
}

function useEditTenancy(
    propertyId: number,
    tenancyId: number,
    {
        reset,
        onClose,
        onError,
        onTenancyConfirmed,
    }: {
        reset: () => void;
        onClose: () => void;
        onError?: (error: Error) => void;
        onTenancyConfirmed?: () => void;
    }
) {
    const toast = useToast();
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (data: FormValues) => {
            await editTenancy(propertyId, tenancyId, data);
        },
        onSuccess: () => {
            toast({
                title: 'Tenancy updated',
                description: 'Tenancy successfully updated',
                status: 'success',
                isClosable: true,
            });

            void queryClient.invalidateQueries({
                queryKey: ['property', propertyId],
            });

            reset();
            onClose();
            onTenancyConfirmed?.();
        },
        onError,
    });

    return {
        isPending: mutation.isPending,
        error: mutation.error,
        edit: mutation.mutate,
        reset: mutation.reset,
    };
}

export {
    useTenancy,
    useAddTenancy,
    useEditTenancy,
};
